import { syntaxTree } from "@codemirror/language";
import type { EditorView } from "@codemirror/view";
import type { Diagnostic } from "@codemirror/lint";
import {
  BsonConstructor,
  BsonType,
  PropertyKey as PropertyKeyNode,
  String as StringNode,
  ArgList,
  TemplateVariable,
} from "./parser.terms";
import { BSON_TYPE_MAP } from "./bsonTypes";
import { ALL_OPERATORS } from "./operators";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface MongoLintConfig {
  fieldPaths?: string[];
  operation?: string;
}

// ---------------------------------------------------------------------------
// Levenshtein distance (two-row DP)
// ---------------------------------------------------------------------------

function levenshtein(a: string, b: string): number {
  const la = a.length;
  const lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;

  let prev: number[] = Array.from({ length: lb + 1 }, (_, j) => j);
  let curr: number[] = Array.from<number>({ length: lb + 1 });

  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j]! + 1,      // deletion
        curr[j - 1]! + 1,  // insertion
        prev[j - 1]! + cost // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }

  return prev[lb]!;
}

function findClosest(name: string, candidates: Iterable<string>, maxDist = 2): string | null {
  let best: string | null = null;
  let bestDist = maxDist + 1;
  for (const c of candidates) {
    const d = levenshtein(name, c);
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripQuotes(text: string): string {
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    return text.slice(1, -1);
  }
  return text;
}

// ---------------------------------------------------------------------------
// Template variable validation
// ---------------------------------------------------------------------------

const TEMPLATE_VAR_RE =
  /^\{\{(?:secret:|config:)?[A-Za-z][A-Za-z0-9_]*\}\}$/;

// ---------------------------------------------------------------------------
// Linter
// ---------------------------------------------------------------------------

export function mongoLinter(
  config: MongoLintConfig = {},
): (view: EditorView) => Diagnostic[] {
  return (view: EditorView): Diagnostic[] => {
    const diagnostics: Diagnostic[] = [];
    const tree = syntaxTree(view.state);
    const doc = view.state.doc;

    // Strip array notation ([], [].) from fieldPaths so "[]._id" matches "_id"
    const fieldSet = config.fieldPaths?.length
      ? new Set(config.fieldPaths.flatMap((p) => {
          const bare = p.replace(/^\[\]\.*/, "").replace(/\[\]/g, "");
          return bare && bare !== p ? [p, bare] : [p];
        }))
      : null;

    const cursor = tree.cursor();

    do {
      const { from, to } = cursor;

      // ---------------------------------------------------------------
      // 8. Syntax errors (error nodes have id 0)
      // ---------------------------------------------------------------
      if (cursor.type.isError) {
        diagnostics.push({
          from,
          to: Math.max(to, from + 1),
          severity: "error",
          message: "Syntax error",
        });
        continue;
      }

      // ---------------------------------------------------------------
      // BsonConstructor checks (rules 1-4)
      // ---------------------------------------------------------------
      if (cursor.type.id === BsonConstructor) {
        const inner = cursor.node;
        const typeNode = inner.getChild(BsonType);
        if (typeNode) {
          const typeName = doc.sliceString(typeNode.from, typeNode.to);
          const info = BSON_TYPE_MAP.get(typeName);

          // Rule 1: Unknown BSON constructor
          if (!info) {
            diagnostics.push({
              from: typeNode.from,
              to: typeNode.to,
              severity: "warning",
              message: `Unknown BSON type '${typeName}'`,
            });
          } else {
            // Rules 2-4: Validate arg using the type's validator
            const argListNode = inner.getChild(ArgList);
            if (argListNode && info.validateArg) {
              const argText = firstStringArgFromNode(doc, argListNode);
              if (argText !== null) {
                const err = info.validateArg(argText);
                if (err) {
                  diagnostics.push({
                    from: argListNode.from,
                    to: argListNode.to,
                    severity: "error",
                    message: err,
                  });
                }
              }
            }
          }
        }
        continue;
      }

      // ---------------------------------------------------------------
      // 5. Unknown operator / 6. Field not in schema
      // ---------------------------------------------------------------
      if (cursor.type.id === PropertyKeyNode) {
        const raw = doc.sliceString(from, to);
        const name = stripQuotes(raw);

        if (name.startsWith("$")) {
          // Rule 5: Unknown operator
          if (!ALL_OPERATORS.has(name)) {
            let msg = `Unknown operator '${name}'`;
            const suggestion = findClosest(name, ALL_OPERATORS.keys());
            if (suggestion) {
              msg += `. Did you mean '${suggestion}'?`;
            }
            diagnostics.push({ from, to, severity: "warning", message: msg });
          }
        } else if (fieldSet) {
          // Rule 6: Field not in schema
          if (!fieldSet.has(name)) {
            let msg = `Field '${name}' not found in collection schema`;
            const suggestion = findClosest(name, fieldSet);
            if (suggestion) {
              msg += `. Did you mean '${suggestion}'?`;
            }
            diagnostics.push({ from, to, severity: "info", message: msg });
          }
        }
        continue;
      }

      // ---------------------------------------------------------------
      // 7. Invalid template variable syntax
      // ---------------------------------------------------------------
      if (cursor.type.id === TemplateVariable) {
        const text = doc.sliceString(from, to);
        if (!TEMPLATE_VAR_RE.test(text)) {
          diagnostics.push({
            from,
            to,
            severity: "error",
            message: "Invalid template variable syntax",
          });
        }
        continue;
      }
    } while (cursor.next());

    return diagnostics;
  };
}

// ---------------------------------------------------------------------------
// Internal: extract first string arg from an ArgList syntax node
// ---------------------------------------------------------------------------

function firstStringArgFromNode(
  doc: { sliceString(from: number, to: number): string },
  argListNode: { cursor(): ReturnType<ReturnType<typeof syntaxTree>["cursor"]> },
): string | null {
  const c = argListNode.cursor();
  if (!c.firstChild()) return null;
  do {
    if (c.type.id === StringNode) {
      return stripQuotes(doc.sliceString(c.from, c.to));
    }
  } while (c.nextSibling());
  return null;
}
